import classNames from 'classnames';
import { ReactNode } from 'react';
import Button from '@/shared/components/shared/Button';
import Image from '@/shared/components/shared/Image';
import Responsive from '@/shared/components/shared/Responsive';
import Reveal from '@/shared/components/shared/Reveal';
import { useTypograf } from '@/shared/hooks/use-typograf';
import { ImageShape } from '@/shared/lib/types';

export type DraftSectionProps = {
    title?: string;
    text: string;
    images: ImageShape[];
    button?: {
        text: string;
        href?: string;
        onClick?: () => void;
    };
};

type Props = React.HTMLAttributes<HTMLElement> & {
    data: DraftSectionProps;
    isReversed?: boolean;
    children?: ReactNode;
};

const DraftSection = ({ data, isReversed, children, ...props }: Props) => {
    const tpDescription = useTypograf(data.text);
    const tpTitle = useTypograf(data.title || '');

    return (
        <section
            {...props}
            className={classNames('draft-section wrapper js-header-theme-trigger', props.className, {
                'draft-section--reversed': isReversed,
            })}
            data-theme="light"
            id={props.id || 'drafts'}
        >
            <div className="draft__descr-wrapper">
                <Reveal delay={200}>
                    <div className="draft__descr wysiwyg">
                        {data.title && <div className="draft__descr-title h3">{tpTitle}</div>}
                        {tpDescription && (
                            <div
                                className={classNames('text-m', {
                                    wysiwyg: data.button || data.title,
                                })}
                                dangerouslySetInnerHTML={{ __html: tpDescription }}
                            />
                        )}
                        {data.button && (
                            <Button
                                tag={data.button.href ? 'a' : 'button'}
                                href={data.button.href}
                                target={data.button.href?.startsWith('http') ? '_blank' : undefined}
                                rel={data.button.href?.startsWith('http') ? 'noreferrer nofollow' : undefined}
                                className="draft__descr-btn"
                                variant="outline-light"
                                onClick={data.button.onClick}
                            >
                                {data.button.text}
                            </Button>
                        )}
                        {children}
                    </div>
                </Reveal>
            </div>
            <div className="draft__list-container">
                <ul className="draft__list list-unstyled">
                    {data.images.map((item, index) => (
                        <li className="draft__item" key={index}>
                            <Reveal>
                                <Responsive className="event-card">
                                    <Image
                                        src={item.src}
                                        className="responsive__item"
                                        height={item.height}
                                        width={item.width}
                                        alt={item.alt || ''}
                                        sizes="(max-width: 1199px) 100vw, 30vw"
                                    />
                                </Responsive>
                            </Reveal>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
};

export default DraftSection;
